import { Box, IconButton, SxProps, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { AnchorText } from 'components/Text';
import React, { useEffect, useRef, useState } from 'react';
import { rest } from 'services/api';
import { useToast, ToastType } from 'services/toast';
import { formatMediaDuration } from 'utils/fomatting';
import { TrackingEventName } from 'services/tracking/constants';
import { useTracking } from 'services/tracking/hooks';

const playerContainer: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  gap: 1,
  alignItems: 'center'
};

type Props = {
  twilioMediaSid: string;
};

const MediaAudioPlayer: React.FC<Props> = ({ twilioMediaSid }) => {
  const { showToast } = useToast();

  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const { track } = useTracking();

  const playMedia = async () => {
    track(TrackingEventName.CampaignDashboardConversationPlayVoiceNote);

    if (!downloadUrl) {
      await fetchDownloadUrl();
    }

    setIsPlaying(true);
  };

  const pauseMedia = () => {
    setIsPlaying(false);
  };

  const fetchDownloadUrl = async () => {
    try {
      setIsLoading(true);
      const { downloadUrl } = await rest.get(
        '/data_extractor/fetch-message-media',
        {
          twilioMediaSid
        }
      );

      setDownloadUrl(downloadUrl);
    } catch (e) {
      showToast({
        message:
          'Error downloading the audio file. Try again or contact support.',
        type: ToastType.Error
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (audioRef.current && downloadUrl) {
      if (isPlaying) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }
  }, [downloadUrl, isPlaying]);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (audioElement) {
      const updateCurrentTime = () => {
        setCurrentTime(audioElement.currentTime);
      };

      const setAudioDuration = () => {
        setDuration(audioElement.duration);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      audioElement.addEventListener('timeupdate', updateCurrentTime);
      audioElement.addEventListener('loadedmetadata', setAudioDuration);
      audioElement.addEventListener('ended', handleEnded);

      return () => {
        audioElement.removeEventListener('timeupdate', updateCurrentTime);
        audioElement.removeEventListener('loadedmetadata', setAudioDuration);
        audioElement.removeEventListener('ended', handleEnded);
        audioElement.pause();
        audioElement.currentTime = 0; // Reset the audio to the beginning
      };
    }
  }, [downloadUrl]);

  if (isLoading) {
    return <Typography variant="body2">Loading</Typography>;
  }

  return (
    <Box sx={playerContainer}>
      <audio ref={audioRef} src={downloadUrl} />
      {isPlaying ? (
        <>
          <IconButton onClick={pauseMedia}>
            <PauseIcon color="primary" fontSize="small" />
          </IconButton>
          <AnchorText onClick={pauseMedia}>Pause</AnchorText>
          <Typography variant="body2">
            {formatMediaDuration(duration - currentTime)}
          </Typography>
        </>
      ) : (
        <>
          <IconButton onClick={playMedia}>
            <PlayArrowIcon color="primary" fontSize="small" />
          </IconButton>
          <AnchorText onClick={playMedia}>Play voice note</AnchorText>
          {duration > 0 && (
            <Typography variant="body2">
              {formatMediaDuration(duration - currentTime)}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export { MediaAudioPlayer };
